import pluralize from 'pluralize'
import { useState } from 'react'

import { IllustrationName } from '@/components/icons/Illustration'
import { COVERAGE_ICONS } from '@/components/quotes/summary/coverageIcons'
import { COVERAGE_TYPE_LABELS, type CoverageType } from '@/models/insuranceCoverage'
import { formatDate, formatPhone, listSentence } from '../../lib/formatters'
import { formatAddress } from '../../models/address'
import { yearsInBusinessLabel } from '../../models/business'
import { ChatItem } from '../../models/chat'
import { useInsuranceApplication } from '../../models/insuranceApplication'
import { isFleetReadonly, maritalStatusLabel, useCurrentUser, useOrganisation } from '../../models/user'
import { ChatAddDrivers } from './ChatAddDrivers'
import { ChatAddEquipments } from './ChatAddEquipments'
import { EditButton, IllustratedRow, InfoRow, SectionCard } from './ConfirmInfoCard'
import { EditBusinessDetailsDialog } from './EditBusinessDetailsDialog'
import { EditCoverageNeedsDialog } from './EditCoverageNeedsDialog'
import { EditFreightDetailsDialog } from './EditFreightDetailsDialog'
import { EditLossRunsDialog } from './EditLossRunsDialog'
import { EditOwnerInfoDialog } from './EditOwnerInfoDialog'
import { EditTravelDistanceDialog } from './EditTravelDistanceDialog'
import { IssueBanner } from './IssueBanner'

interface ChatConfirmInformationProps {
  freightDetailsChat: ChatItem[]
  orgId?: string
  readonly?: boolean
}

function entityTypeLabel(value: string | null | undefined): string {
  switch (value) {
    case 'individual':
      return 'Individual'
    case 'partnership':
      return 'Partnership'
    case 'llc':
      return 'Corporation/LLC'
    default:
      return '-'
  }
}

const ELD_PROVIDER_LABELS: Record<string, string> = {
  motive: 'Motive',
  samsara: 'Samsara',
  geotab: 'Geotab',
  verizon_connect: 'Verizon Connect',
}

export function eldProviderLabel(
  hasEld: boolean | null | undefined,
  provider: string | null | undefined
): string {
  if (provider) return ELD_PROVIDER_LABELS[provider] ?? provider
  // Only an explicit "no" reads as None; an unasked question stays blank.
  if (hasEld === false) return 'None'
  return '-'
}

export const ChatConfirmInformation = ({
  freightDetailsChat: _freightDetailsChat,
  orgId,
  readonly,
}: ChatConfirmInformationProps) => {
  const { user, refetch: refetchUser } = useCurrentUser({ enabled: !orgId })
  const { organisation, refetch: refetchOrg } = useOrganisation(orgId)
  const org = orgId ? organisation : user?.currentOrg
  const refetch = orgId ? refetchOrg : refetchUser
  const business = org?.business
  const application = org?.currentInsuranceApplication
  // The chat's own query, already loaded: it carries this application's coverage menu, Pet included for an owner-operator.
  const { data: fullApplication } = useInsuranceApplication({ id: application?.id })
  const address = business?.address
  const isDisabled = readonly || isFleetReadonly(user)
  const businessOwner = application?.businessOwner
  const validation = application?.confirmInformationValidation ?? []
  const cardMissing = (key: string): string[] => {
    const card = validation.find((c) => c.key === key)
    if (!card || card.valid) return []
    return card.missing
  }
  const cardInvalid = (key: string): string[] => {
    const card = validation.find((c) => c.key === key)
    if (!card || card.valid) return []
    return card.invalid ?? []
  }
  // Not gated on `valid`: a warning shows on an otherwise-passing card.
  const cardWarnings = (key: string): string[] => validation.find((c) => c.key === key)?.warnings ?? []
  const selectedCoverages: CoverageType[] = application?.selectedCoverages ?? []
  const businessMissing = cardMissing('business')
  const ownerMissing = cardMissing('owner')
  const distanceMissing = cardMissing('distance')
  const freightMissing = cardMissing('freight')
  const driversMissing = cardMissing('drivers')
  const driversWarnings = cardWarnings('drivers')
  const equipmentMissing = cardMissing('equipment')
  const equipmentInvalid = cardInvalid('equipment')
  const equipmentWarnings = cardWarnings('equipment')
  const rosterMissing = [...driversMissing, ...equipmentMissing]
  const [editBusinessOpen, setEditBusinessOpen] = useState(false)
  const [editCoverageOpen, setEditCoverageOpen] = useState(false)
  const [editOwnerOpen, setEditOwnerOpen] = useState(false)
  const [editDistanceOpen, setEditDistanceOpen] = useState(false)
  const [editFreightOpen, setEditFreightOpen] = useState(false)
  const [editLossRunsOpen, setEditLossRunsOpen] = useState(false)
  const lossRunSummary = application?.lossRunSummary
  // One row per loss run history entry — the same rows the Edit loss run history form lists.
  const claimRows = Object.values(lossRunSummary?.defaultValues ?? {}).flat()
  const lossRunsMissing =
    !lossRunSummary?.isParsing && claimRows.length === 0 && !lossRunSummary?.hasReportOnFile
  const claimsFooter = lossRunsMissing
    ? 'Insurance providers will require these before checkout.'
    : !lossRunSummary?.isParsing && claimRows.length > 0
      ? 'Have more loss run reports? You can provide them after submitting your application.'
      : undefined

  function formatDob(dob: string | null | undefined): string {
    if (!dob) return '-'
    const date = new Date(dob + 'T00:00:00')
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  return (
    <div className="flex w-full flex-col gap-2">
      {org && (
        <EditBusinessDetailsDialog
          open={editBusinessOpen}
          onOpenChange={setEditBusinessOpen}
          onSuccess={refetch}
          business={business}
          entityType={application?.entityType}
          hasEld={application?.hasEld}
          eldProvider={application?.eldProvider}
          coverageLapseDetected={application?.coverageLapseDetected}
          cancellationReason={application?.cancellationReason}
          orgId={org.id}
        />
      )}
      <SectionCard
        title="Business details"
        action={isDisabled ? null : <EditButton onClick={() => setEditBusinessOpen(true)} />}
        error={businessMissing.length > 0}
      >
        <InfoRow label="DOT number" value={business?.dotNumber || '-'} />
        <InfoRow label="Company name" value={business?.name || '-'} />
        <InfoRow label="Business type" value={entityTypeLabel(application?.entityType)} />
        <InfoRow label="Address" value={address ? formatAddress(address) : '-'} />
        <InfoRow label="Years in business" value={yearsInBusinessLabel(business?.yearsInBusiness)} />
        <InfoRow
          label="ELD provider"
          value={eldProviderLabel(application?.hasEld, application?.eldProvider)}
        />
        {address?.state === 'CA' && <InfoRow label="CA number" value={business?.caNumber || '-'} />}
        {application?.coverageLapseDetected && (
          <InfoRow label="Coverage lapse reason" value={application.cancellationReason || '-'} />
        )}
        <IssueBanner
          variant="error"
          lines={businessMissing.length ? [`Add ${listSentence(businessMissing)}`] : []}
          onClick={isDisabled ? undefined : () => setEditBusinessOpen(true)}
        />
      </SectionCard>

      {org && (
        <EditCoverageNeedsDialog
          open={editCoverageOpen}
          onOpenChange={setEditCoverageOpen}
          onSuccess={refetch}
          startDate={application?.desiredEffectiveDate ?? null}
          selected={selectedCoverages}
          orgId={org.id}
          menu={fullApplication?.coverageMenu}
        />
      )}
      <SectionCard
        title="Coverage needs"
        action={isDisabled ? null : <EditButton onClick={() => setEditCoverageOpen(true)} />}
      >
        <InfoRow label="Coverage starts" value={formatDate(application?.desiredEffectiveDate)} />
        <div className="flex flex-col gap-2">
          {selectedCoverages.length ? (
            selectedCoverages.map((coverage) => {
              const Icon = COVERAGE_ICONS[coverage]
              return (
                <span key={coverage} className="text-foreground flex items-center gap-2">
                  <Icon className="text-ink-500 size-5 shrink-0" />
                  {COVERAGE_TYPE_LABELS[coverage]}
                </span>
              )
            })
          ) : (
            <span className="text-foreground">-</span>
          )}
        </div>
      </SectionCard>

      {org && (
        <EditOwnerInfoDialog
          open={editOwnerOpen}
          onOpenChange={setEditOwnerOpen}
          onSuccess={refetch}
          owner={businessOwner ?? undefined}
          orgId={org.id}
        />
      )}
      <SectionCard
        title="Owner info"
        action={isDisabled ? null : <EditButton onClick={() => setEditOwnerOpen(true)} />}
        error={ownerMissing.length > 0}
      >
        {businessOwner?.driverId && (
          <p className="text-muted-foreground text-xs">
            Synced from the driver marked as the business owner below.
          </p>
        )}
        <InfoRow
          label="Owner name"
          value={businessOwner ? `${businessOwner.firstName} ${businessOwner.lastName}`.trim() || '-' : '-'}
        />
        <InfoRow label="Email" value={businessOwner?.email || '-'} />
        <InfoRow label="Phone number" value={formatPhone(businessOwner?.mobile) || '-'} />
        <InfoRow label="Date of birth" value={formatDob(businessOwner?.dob)} />
        <InfoRow label="Marital status" value={maritalStatusLabel(businessOwner?.maritalStatus)} />
        <IssueBanner
          variant="error"
          lines={ownerMissing.length ? [`Add ${listSentence(ownerMissing)}`] : []}
          onClick={isDisabled ? undefined : () => setEditOwnerOpen(true)}
        />
      </SectionCard>

      {org && (
        <EditTravelDistanceDialog
          open={editDistanceOpen}
          onOpenChange={setEditDistanceOpen}
          onSuccess={refetch}
          options={application?.travelDistanceOptions || []}
          selected={application?.travelDistance || null}
          orgId={org.id}
        />
      )}
      <SectionCard
        title="Driving distance"
        action={isDisabled ? null : <EditButton onClick={() => setEditDistanceOpen(true)} />}
        error={distanceMissing.length > 0}
      >
        <div className="flex flex-col gap-1">
          {application?.travelDistance?.map((distance) => (
            <span key={distance.value} className="text-foreground">
              {distance.label}
            </span>
          )) || <span className="text-foreground">-</span>}
        </div>
        <IssueBanner
          variant="error"
          lines={distanceMissing.length ? [`Add ${listSentence(distanceMissing)}`] : []}
          onClick={isDisabled ? undefined : () => setEditDistanceOpen(true)}
        />
      </SectionCard>

      {org && (
        <EditFreightDetailsDialog
          open={editFreightOpen}
          onOpenChange={setEditFreightOpen}
          onSuccess={refetch}
          commodities={application?.commodities || []}
          orgId={org.id}
        />
      )}
      <SectionCard
        title="Freight details"
        action={isDisabled ? null : <EditButton onClick={() => setEditFreightOpen(true)} />}
        error={freightMissing.length > 0}
      >
        <div className="flex flex-col gap-1">
          {application?.commodities?.length ? (
            application.commodities.map((item) => (
              <span key={item.name} className="text-foreground">
                {item.name} ({item.percentage}%)
              </span>
            ))
          ) : (
            <span className="text-foreground">-</span>
          )}
        </div>
        <IssueBanner
          variant="error"
          lines={freightMissing.length ? [`Add ${listSentence(freightMissing)}`] : []}
          onClick={isDisabled ? undefined : () => setEditFreightOpen(true)}
        />
      </SectionCard>

      {org && (
        <EditLossRunsDialog
          open={editLossRunsOpen}
          onOpenChange={setEditLossRunsOpen}
          onSuccess={refetch}
          defaultValues={lossRunSummary?.defaultValues}
          isParsing={lossRunSummary?.isParsing}
          orgId={org.id}
        />
      )}
      <SectionCard
        title="Claims"
        action={isDisabled ? null : <EditButton onClick={() => setEditLossRunsOpen(true)} />}
        loading={lossRunSummary?.isParsing}
        warning={lossRunsMissing}
        footer={claimsFooter}
      >
        <IllustratedRow
          illustration={IllustrationName.LOSS_RUN}
          muted={lossRunsMissing}
          label={
            lossRunSummary?.isParsing ? (
              <span className="text-foreground">
                Updating your application with loss run info.
                <br />
                This may take a few minutes...
              </span>
            ) : claimRows.length > 0 ? (
              <span className="text-foreground">
                {claimRows.length} {pluralize('loss run', claimRows.length)} added
              </span>
            ) : lossRunSummary?.hasReportOnFile ? (
              <span className="text-foreground">0 losses (clean loss run report on file)</span>
            ) : (
              <span className="text-muted-foreground">Loss run reports not yet provided.</span>
            )
          }
        />
      </SectionCard>

      <SectionCard
        title="Drivers & equipment"
        error={driversMissing.length > 0 || equipmentMissing.length > 0 || equipmentInvalid.length > 0}
        warning={driversWarnings.length > 0 || equipmentWarnings.length > 0}
      >
        <div className="flex flex-col gap-3">
          <ChatAddDrivers edit slim enforceCarrierRequired orgId={orgId} readonly={readonly} />
          <ChatAddEquipments
            edit
            slim
            enforceCarrierRequired
            type="trucks"
            orgId={orgId}
            readonly={readonly}
          />
          <ChatAddEquipments
            edit
            slim
            enforceCarrierRequired
            type="trailers"
            orgId={orgId}
            readonly={readonly}
          />
        </div>
        <IssueBanner
          variant="error"
          lines={[
            // One line each: a driver's or a truck's own list of gaps doesn't join into a sentence.
            ...rosterMissing.map((label) => `Add ${label}`),
            ...equipmentInvalid,
          ]}
        />
        <IssueBanner variant="warning" lines={[...driversWarnings, ...equipmentWarnings]} />
      </SectionCard>
    </div>
  )
}
